//
// A Fluxible store for the designs in a brief
//

import BaseStore from 'fluxible/addons/BaseStore';
import Immutable from 'immutable';

const MIN_SELECTION = 3;
const MAX_SELECTION = 10;

interface Designer {
    id: string;
    displayName: string;
}
interface Design {
    id: number;
    url?: string;
    designer?: Designer;
}

class BriefDesignStore extends BaseStore {
    static storeName = 'BriefDesignStore';
    static handlers = {
        TOGGLE_DESIGN: 'toggleDesign',
        LOAD_DRAFT_DATA: 'load',
        CLEAR_DESIGNS: 'clearDesigns',
        SAVE_DRAFT_SUCCESS: 'updateDesigns',
    };

    designs = Immutable.OrderedSet<Design>([]);
    selectedDesigns = Immutable.OrderedSet<Design>([]);

    load(payload: {
        designs: Design[];
        body: {
            sample_designs: number[];
            inspiration_design_urls: string;
            designers: Designer[];
        };
    }) {
        if (payload.designs) {
            this.setDesigns(payload.designs);
        }

        if (payload.body.sample_designs) {
            let inspiration_urls: string[];
            if (payload.body.inspiration_design_urls) {
                inspiration_urls = JSON.parse(payload.body.inspiration_design_urls);
            }
            const selectedDesigns = payload.body.sample_designs.map((design, idx) => ({
                id: design,
                url: inspiration_urls ? inspiration_urls[idx] : '',
                designer: payload.body.designers ? payload.body.designers[idx] : undefined,
            }));
            this.setSelectedDesigns(selectedDesigns);
        }
        this.emitChange();
    }

    updateDesigns(payload: { designs: Design[] }) {
        if (payload.designs) {
            this.setDesigns(payload.designs);
        }
        this.emitChange();
    }

    clearDesigns() {
        this.setDesigns([]);
        this.setSelectedDesigns([]);
    }

    setDesigns(designs: Design[]) {
        this.designs = Immutable.OrderedSet(designs);
    }

    setSelectedDesigns(designs: Design[]) {
        this.selectedDesigns = Immutable.Set(designs);
    }

    toggleDesign(design: Design) {
        const designExists = this.selectedDesigns.some((item: Design) => item.id === design.id);
        if (!designExists) {
            this.selectedDesigns = this.selectedDesigns.add(design);
        } else {
            this.selectedDesigns = this.selectedDesigns.delete(
                this.selectedDesigns.find((item: Design) => item.id === design.id)
            );
        }
        this.emitChange();
    }

    getDesigns() {
        return this.designs;
    }

    getSelectedDesigns() {
        return this.selectedDesigns;
    }

    getPercentageComplete() {
        return (this.selectedDesigns.count() / MAX_SELECTION) * 100;
    }

    isSelectionDisabled() {
        return this.getPercentageComplete() >= 100;
    }

    isMinimumAmountSelected() {
        return this.selectedDesigns.count() >= MIN_SELECTION;
    }

    findDesign(id: number) {
        return this.designs.find((design: Design) => {
            return design.id === id;
        });
    }
}

export default BriefDesignStore;
